import { useState } from 'react';
import type { ReactNode } from 'react';
import type { EnsaioIsc } from '../../api/isc';
import { BlocoDaFicha } from '../../Ficha';
import { numero } from '../../formato';
import { useSessao } from '../../sessao';
import { ErroDaApi, Selo, TabelaDeLancamento } from '../../ui';
import type { ColunaDeLancamento, LinhaDeLancamento } from '../../ui';
import { CASAS, CurvaPressao, Expansao, Penetracao } from '../Isc';
import type { GravarFn } from '../Isc';
import { MolduraDaRepeticao, useRepeticao } from './Repeticao';
import type { Comparacao, UseRepeticao } from './Repeticao';
import { comparacaoNumerica, diferencaComSinal } from './RepeticaoNumerica';
import type { ResultadoNumerico } from './RepeticaoNumerica';

/**
 * Repetir o ISC sozinho na Interpretação (onda 323, D10).
 *
 * «Os mesmos corpos de prova»: abrir a repetição copia os CPs vivos da
 * vigente, com a moldagem do ponto da compactação e sem leitura nenhuma. Aqui
 * só se relançam as leituras de expansão e de penetração — pelas mesmas peças
 * da etapa dos corpos de prova, e pelas rotas de sempre — e não se lança
 * compactação nova. À direita, a curva pressão × penetração do CP escolhido,
 * inicial tracejada × repetição; embaixo, o ISC e a expansão de cada CP lado a
 * lado. O ISC do ensaio (decisão de projeto, NBR 9895, 6.4.2) se adota depois
 * de adotar a repetição, na etapa «ISC adotado», como sempre.
 */

/* Funções, e não constantes: `CASAS` mora em `Isc.tsx`, que importa este
   arquivo — lida na carga do módulo, ainda não existiria. */
const ISC_OTIMA = (): ResultadoNumerico => ({
  rotulo: 'ISC na w ótima', unidade: '%', casas: CASAS.isc, unidadeDaDiferenca: 'p.p.',
  valor: (f) => (f?.cabecalho?.isc_na_umidade_otima != null
    ? Number(f.cabecalho.isc_na_umidade_otima) : null),
  nota: () => 'interpolado — apoio, não o ISC do ensaio',
  semDiferenca: 'sai com as penetrações da repetição',
});
const EXPANSAO = (): ResultadoNumerico => ({
  rotulo: 'expansão máxima', unidade: '%', casas: CASAS.expansao, unidadeDaDiferenca: 'p.p.',
  valor: (f) => (f?.cabecalho?.expansao_maxima != null ? Number(f.cabecalho.expansao_maxima) : null),
  semDiferenca: 'sai com as leituras de expansão da repetição',
});

const comparacao = (i: EnsaioIsc, a: EnsaioIsc | null): Comparacao[] => [
  { ...comparacaoNumerica(ISC_OTIMA(), i, a), rotulo: 'ISC na w ótima', chave: 'isc-otima' },
  { ...comparacaoNumerica(EXPANSAO(), i, a), rotulo: 'Expansão máxima', chave: 'expansao' },
];

const vivos = (f: any) => (f?.corpos ?? []).filter((x: any) => !x.descartado);
/** O mesmo CP na outra tentativa: a cópia aponta para o mesmo ponto da compactação. */
const oMesmo = (f: any, cp: any) => (f?.corpos ?? []).find((x: any) => x.ponto_id === cp?.ponto_id);

/**
 * O ISC e a expansão de cada CP, inicial × repetição. Com `atual` nulo (a
 * Interpretação de sempre, as tentativas anteriores), só a tentativa dada.
 */
export function CorposLadoALado({ inicial, atual, vigente, numero: n }: {
  inicial: any; atual: any; vigente?: number; numero?: number;
}) {
  const dois = atual != null;
  const cps = dois ? vivos(atual) : vivos(inicial);
  const isc = (x: any) => (x?.isc != null ? numero(x.isc, CASAS.isc) : '—');
  const exp = (x: any) => (x?.expansao_percent != null ? numero(x.expansao_percent, CASAS.expansao) : '—');
  const dif = (a: any, b: any, k: string, casas: number) => (a?.[k] != null && b?.[k] != null
    ? diferencaComSinal(Number(b[k]) - Number(a[k]), casas) : '—');
  const colunas: ColunaDeLancamento[] = dois
    ? [{ texto: 'CP' }, { texto: 'w (%)', num: true }, { texto: 'γd (g/cm³)', num: true },
      { texto: `ISC · tentativa ${vigente ?? ''} (%)`, num: true },
      { texto: `ISC · tentativa ${n ?? ''} (%)`, num: true }, { texto: 'Dif. ISC', num: true },
      { texto: `Expansão · tentativa ${vigente ?? ''} (%)`, num: true },
      { texto: `Expansão · tentativa ${n ?? ''} (%)`, num: true }]
    : [{ texto: 'CP' }, { texto: 'w (%)', num: true }, { texto: 'γd (g/cm³)', num: true },
      { texto: 'ISC (%)', num: true }, { texto: 'Expansão (%)', num: true }];
  const linhas: LinhaDeLancamento[] = cps.map((x: any) => {
    const i = dois ? oMesmo(inicial, x) : x;
    const moldagem = [x.ordem, numero(x.umidade_media, 1), numero(x.massa_especifica_seca, 3)];
    return {
      chave: x.id,
      titulo: <>CP {x.ordem} · <span className="cod">{x.cilindro}</span></>,
      resultado: <span className="n">ISC {isc(x)} %</span>,
      semCartao: [0],
      celulas: dois
        ? [...moldagem,
          <span className="n" data-cp={x.ordem} data-lado="inicial">{isc(i)}</span>,
          <span className="n" data-cp={x.ordem} data-lado="atual">{isc(x)}</span>,
          <b className="n" data-cp={x.ordem} data-lado="diferenca">{dif(i, x, 'isc', CASAS.isc)}</b>,
          exp(i), exp(x)]
        : [...moldagem, isc(x), exp(x)],
    };
  });
  return (
    <BlocoDaFicha titulo={dois ? 'ISC e expansão de cada CP · inicial × repetição' : 'ISC e expansão de cada CP'}
      sub={dois
        ? 'Os mesmos corpos de prova: w e γd são os da moldagem na compactação, comuns às duas tentativas.'
        : 'O ISC do CP é o maior entre 2,54 e 5,08 mm (Tabela 4, NOTA).'}>
      {cps.length === 0
        ? <p className="sub fe-miolo">Nenhum corpo de prova válido.</p>
        : (
          <div className="fe-tabela">
            <TabelaDeLancamento rotulo="ISC e expansão de cada corpo de prova"
              classe="fe-lanc repeticao-corpos" colunas={colunas} linhas={linhas} />
          </div>
        )}
    </BlocoDaFicha>
  );
}

export function RepeticaoIsc({ id, congelado, aoMudar, children }: {
  id: string;
  congelado: boolean;
  aoMudar(): void | Promise<void>;
  /** A Interpretação de sempre (o ISC e a expansão de cada CP). */
  children: ReactNode;
}) {
  const { pode } = useSessao();
  const podeInterpretar = pode('ensaio.interpretar');
  /* R1: com a repetição aberta, quem interpreta também lança nela. */
  const lanca = pode('ensaio.lancar') || podeInterpretar;
  const rep = useRepeticao<EnsaioIsc>(id, 'isc', aoMudar);
  const [cpId, setCpId] = useState<string | null>(null);
  const [mostrarInicial, setMostrarInicial] = useState(true);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  /* As peças da etapa dos corpos de prova gravam por aqui: o botão se solta
     logo depois de gravar, e a repetição e a ficha se releem (Tasks 4–5). */
  const gravarCom = (r: UseRepeticao<EnsaioIsc>): GravarFn => async (marca, chamada) => {
    setSalvando(marca); setErro(null);
    try {
      await chamada();
      setSalvando(null);
      await r.recarregar();
      void Promise.resolve(aoMudar()).catch(() => undefined);
      return true;
    } catch (e) { setErro(e); return false; } finally { setSalvando(null); }
  };

  const escolhido = (r: UseRepeticao<EnsaioIsc>) => {
    const atual = r.dados?.atual;
    return (atual?.corpos ?? []).find((x: any) => x.id === cpId) ?? vivos(atual)[0] ?? null;
  };

  const lancamento = (r: UseRepeticao<EnsaioIsc>) => {
    const atual = r.dados?.atual;
    const cp = escolhido(r);
    const cpIni = oMesmo(r.dados?.inicial, cp);
    const gravar = gravarCom(r);
    return (
      <BlocoDaFicha titulo={`Corpos de prova · tentativa ${r.dados?.aberta?.numero ?? ''}`}
        sub="Os mesmos corpos de prova da inicial, com a moldagem do ponto da compactação. Só as leituras de expansão e de penetração se lançam de novo; não há compactação nova.">
        <div className="fe-miolo fe-cps">
          {(atual?.corpos ?? []).length > 0 && (
            <div className="fe-alternador" role="group" aria-label="Corpo de prova da repetição">
              {(atual?.corpos ?? []).map((x: any) => (
                <button key={x.id} type="button" aria-pressed={cp?.id === x.id}
                  className={x.descartado ? 'descartado' : undefined}
                  onClick={() => setCpId(x.id)}>
                  CP {x.ordem}
                  <small>
                    {x.descartado ? 'descartado'
                      : `w ${numero(x.umidade_media, 1)} % · ISC ${x.isc == null ? '—' : `${numero(x.isc, CASAS.isc)} %`}`}
                  </small>
                </button>
              ))}
            </div>
          )}
        </div>
        {!cp && <p className="sub fe-miolo">A repetição não tem corpo de prova vivo.</p>}
        {cp && (
          <>
            <div className="fe-miolo fe-cp-topo repeticao-moldagem">
              <span>
                <b>CP {cp.ordem}</b> · cilindro <span className="cod">{cp.cilindro}</span>
                {' '}· w {numero(cp.umidade_media, 1)} % · γd {numero(cp.massa_especifica_seca, 3)} g/cm³
              </span>
              <Selo tom="neutro">moldagem da compactação</Selo>
              {cpIni?.isc != null && <Selo tom="neutro">inicial: ISC {numero(cpIni.isc, CASAS.isc)} %</Selo>}
            </div>
            {lanca ? (
              <>
                <Penetracao cp={cp}
                  linhas={(atual?.penetracao ?? []).filter((l: any) => l.corpo_prova_id === cp.id)}
                  tabela={atual?.tabelaNorma ?? r.dados?.inicial?.tabelaNorma ?? []}
                  id={id} gravar={gravar} salvando={salvando} secundario />
                <Expansao cp={cp}
                  linhas={(atual?.expansao ?? []).filter((l: any) => l.corpo_prova_id === cp.id)}
                  id={id} gravar={gravar} salvando={salvando} />
              </>
            ) : (
              <p className="sub fe-miolo">
                Lançar pede a permissão de lançar dados de ensaio: as leituras chegam aqui quando o
                laboratório as lança na etapa dos corpos de prova.
              </p>
            )}
            <ErroDaApi erro={erro} />
          </>
        )}
      </BlocoDaFicha>
    );
  };

  const grafico = (r: UseRepeticao<EnsaioIsc>) => {
    const cp = escolhido(r);
    if (!cp) return null;
    const ini = r.dados?.inicial;
    const cpIni = oMesmo(ini, cp);
    return (
      <CurvaPressao cp={cp} repeticao
        linhas={(r.dados?.atual?.penetracao ?? []).filter((l: any) => l.corpo_prova_id === cp.id)}
        inicial={mostrarInicial && cpIni
          ? { cp: cpIni, linhas: (ini?.penetracao ?? []).filter((l: any) => l.corpo_prova_id === cpIni.id) }
          : null}
        titulo={`Pressão × penetração · CP ${cp.ordem} · ${mostrarInicial && cpIni ? 'inicial × atual' : 'repetição'}`}
        acao={(
          <label className="marcavel">
            <input type="checkbox" checked={mostrarInicial}
              onChange={(e) => setMostrarInicial(e.target.checked)} />
            mostrar a inicial
          </label>
        )} />
    );
  };

  /* A curva vem embaixo do lançamento, na largura toda: a tabela da
     penetração tem sete colunas e não cabe ao lado do gráfico. */
  const depois = (r: UseRepeticao<EnsaioIsc>) => (
    <>
      {grafico(r)}
      <CorposLadoALado inicial={r.dados?.inicial} atual={r.dados?.atual}
        vigente={r.dados?.vigente} numero={r.dados?.aberta?.numero} />
      <p className="sub">
        O ISC do ensaio é decisão de projeto: depois de adotar a repetição, ele se adota na etapa
        «ISC adotado», como sempre, com o critério e quem assina.
      </p>
    </>
  );

  return (
    <MolduraDaRepeticao ensaioId={id} ficha="isc" titulo="ISC e expansão"
      podeInterpretar={podeInterpretar} congelado={congelado} repeticao={rep} aoMudar={aoMudar}
      frase={<>Os mesmos corpos de prova, com as leituras de expansão e de penetração lançadas de
        novo; cada leitura entra na curva assim que é gravada. O laudo continua com as leituras
        iniciais até você adotar a repetição.</>}
      comparacao={comparacao} lancamento={lancamento} depois={depois}>
      {children}
    </MolduraDaRepeticao>
  );
}
